import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { NotificationService } from '../../services/notification';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth';

export const SHOULD_HANDLE_ERROR = new HttpContextToken(() => true);

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  router = inject(Router);
  authService = inject(AuthService);
  notification = inject(NotificationService);
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((e) => {
        if (this.shouldHandleError(request)) {
          return of(this.handleError(e));
        }

        throw e;
      })
    );
  }

  private handleError(errorResponse: HttpErrorResponse): HttpEvent<any> {
    const errors = this.extractError(errorResponse);
    const DEFAULT_404_ERROR_TEXT = 'Recurso não encontrado';
    const statusCode = errorResponse.status;
    const hasErrors = errors.length;

    if (statusCode === 401) {
      this.authService.removeAuthenticationData();
      this.router.navigate(['autenticacao', 'entrar'], {
        queryParams: {
          redirectTo: this.router.url,
        },
      });
      this.notification.info('Você não está autenticado');
      throw new Error('Você não está autenticado');
    }

    if (!hasErrors && statusCode === 404) {
      errors.push(DEFAULT_404_ERROR_TEXT);
    }

    errors.forEach((error) => {
      this.notification.info(error);
    });

    throw new Error(errors.join(', '));
  }

  private extractError(errorResponse: HttpErrorResponse): string[] {
    const contentType = errorResponse.headers.get('Content-Type');
    const isTextPlain = contentType?.includes('text/plain');
    const extractSingleError = (e: any) => e.errorMessage ?? e;

    if (errorResponse.error instanceof ArrayBuffer) {
      return [new TextDecoder('utf-8').decode(errorResponse.error)];
    }

    if (isTextPlain) {
      return [errorResponse.error];
    }

    if (typeof errorResponse.error?.message === 'string') {
      return [errorResponse.error.message];
    }

    try {
      if (Array.isArray(errorResponse.error)) {
        return errorResponse.error.map(extractSingleError) ?? [];
      }

      return errorResponse.error?.errors.map(extractSingleError) ?? [];
    } catch {
      return ['Houve um erro na requisição'];
    }
  }

  private shouldHandleError(request: HttpRequest<unknown>): boolean {
    return request.context.get(SHOULD_HANDLE_ERROR);
  }
}
